import Alert from "@mui/material/Alert";
import classNames from "classnames";
import * as React from "react";

import { ViewAsOrgButton } from "@/shared/components";
import CopyToClipboardButton from "@/shared/components/CopyToClipboardButton";
import {
  ResourceView,
  useResourceView,
  type FilterDefinition,
} from "@/shared/components/ResourceView";
import { OrganizationTier, type OrgRecord } from "@/shared/domain/orgs";
import { useListOrgs } from "@/shared/domain/orgs/hooks";
import {
  ColumnVisibility,
  DataTable,
  buildColumnsForPreferences,
  createColumnHelper,
  type ColDef,
  type ColumnPickerOption,
  type DataTableLoadingState,
  type ICellRendererParams,
} from "@/shared/foundation/primitives/Table";

import styles from "./AllOrgsViewer.module.css";
import { filtersToParams } from "./filtersToParams";

const columnHelper = createColumnHelper<OrgRecord>();

const COLUMNS: ColDef<OrgRecord>[] = [
  columnHelper.accessor("name", {
    headerName: "Name",
    flex: 2,
    minWidth: 160,
    cellClass: styles.nameCell,
  }),
  columnHelper.accessor("org_id", {
    headerName: "ID",
    flex: 2,
    minWidth: 220,
    cellClass: styles.idCell,
    cellRenderer: (params: ICellRendererParams<OrgRecord, string>) => {
      const id = params.value;
      if (!id) {
        return null;
      }
      return (
        <span className={styles.idCellInner}>
          <span className={styles.idText}>{id}</span>
          <CopyToClipboardButton
            text={id}
            eventName="CopyLinkClicked"
            eventProps={{ link: id }}
            className={styles.idCopyButton}
          />
        </span>
      );
    },
  }),
  columnHelper.accessor("tier", {
    headerName: "Tier",
    flex: 1,
    minWidth: 90,
    cellRenderer: (
      params: ICellRendererParams<OrgRecord, OrganizationTier>,
    ) => {
      const tier = params.value;
      if (!tier) {
        return null;
      }
      const isPremium = tier === OrganizationTier.Premium;
      return (
        <span
          className={classNames(styles.tierPill, {
            [styles.tierPillPremium]: isPremium,
            [styles.tierPillFree]: !isPremium,
          })}
        >
          {tier}
        </span>
      );
    },
  }),
  columnHelper.accessor("status", {
    headerName: "Status",
    flex: 1,
    minWidth: 100,
  }),
  columnHelper.accessor("created", {
    headerName: "Created",
    flex: 1,
    minWidth: 110,
    valueFormatter: (params) =>
      params.value ? params.value.toString().substring(0, 10) : "",
  }),
  {
    colId: "actions",
    headerName: "",
    width: 110,
    pinned: "right",
    sortable: false,
    resizable: false,
    suppressMovable: true,
    // `hide: false` overrides DataTable's `defaultColDef.hide: true` baseline
    // so the actions column is visible even though it's not toggled on
    // through the column-visibility picker.
    hide: false,
    lockVisible: true,
    cellClass: styles.actionsCell,
    cellRenderer: (params: ICellRendererParams<OrgRecord>) => {
      const org = params.data;
      if (!org) {
        return null;
      }
      return <ViewAsOrgButton org={org} size="small" />;
    },
  },
];

const ACTIONS_COLUMN_ID = "actions";

function colId<T>(col: ColDef<T>): string {
  return col.colId ?? col.field ?? "";
}

const COLUMN_PICKER_OPTIONS: ColumnPickerOption[] = COLUMNS.filter(
  (c) => colId(c) !== ACTIONS_COLUMN_ID,
).map((c) => ({
  id: colId(c),
  label: c.headerName ?? colId(c),
}));

// Toggleable columns shown by default. The actions column is rendered
// outside this system (it always shows via per-ColDef `hide: false`), so
// it must NOT appear here — otherwise the picker badge would count a
// column the picker doesn't list. `status` is available in the picker
// but hidden by default to keep the table focused on the columns admins
// actually scan for.
const HIDDEN_BY_DEFAULT_COLUMN_IDS = new Set(["status"]);
const DEFAULT_VISIBLE_COLUMN_IDS = COLUMN_PICKER_OPTIONS.filter(
  (c) => !HIDDEN_BY_DEFAULT_COLUMN_IDS.has(c.id),
).map((c) => c.id);

const FILTER_DEFINITIONS: FilterDefinition[] = [
  {
    name: "Name",
    field: "name",
    type: "string",
    allowedComparators: ["CONTAINS"],
  },
  {
    name: "Tier",
    field: "tier",
    type: "enum",
    options: [OrganizationTier.Free, OrganizationTier.Premium],
  },
];

interface SummaryChipProps {
  label: string;
  count: number | undefined;
  variant?: "default" | "premium" | "total";
}

function SummaryChip({ label, count, variant = "default" }: SummaryChipProps) {
  return (
    <span
      className={classNames(styles.summaryChip, {
        [styles.summaryChipPremium]: variant === "premium",
        [styles.summaryChipTotal]: variant === "total",
      })}
    >
      <span>{label}</span>
      <span className={styles.summaryChipCount}>
        {count === undefined ? "…" : count}
      </span>
    </span>
  );
}

interface AllOrgsViewerProps {
  /**
   * Fired when the admin clicks any cell in a row. The parent uses this
   * to drive the adjacent settings panel — we deliberately do NOT call
   * `setCurrentOrganization` here so browsing the table doesn't change
   * the admin's actual org context for the rest of the app.
   */
  onSelectOrg?: (org: OrgRecord) => void;
}

export function AllOrgsViewer({ onSelectOrg }: AllOrgsViewerProps = {}) {
  const view = useResourceView<OrgRecord>({
    getRowId: (row) => row.org_id,
    defaultVisibleColumnIds: DEFAULT_VISIBLE_COLUMN_IDS,
  });

  // DataTable applies `defaultColDef.hide: true`, so each visible column
  // must be flipped to `hide: false` at render time. The actions column
  // already carries `hide: false` on its ColDef so it shows regardless.
  // We also stamp `onCellClicked` onto every ColDef so a click on any
  // cell pages the parent panel; the in-cell Link and CopyToClipboard
  // button stop propagation (or navigate away) so they don't double-fire.
  const columns = React.useMemo<ColDef<OrgRecord>[]>(() => {
    const base = buildColumnsForPreferences<OrgRecord>({
      staticColumns: COLUMNS,
      dynamicColumns: [],
      preferenceOrder: DEFAULT_VISIBLE_COLUMN_IDS,
    });

    if (!onSelectOrg) {
      return base;
    }

    return base.map((col) => ({
      ...col,
      onCellClicked: (event) => {
        if (event.data) {
          onSelectOrg(event.data);
        }
      },
    }));
  }, [onSelectOrg]);

  // AG Grid sees the actions column as visible at mount and emits a
  // `columnVisible` event for it; that flows into the hook's set. Shield
  // the picker badge from that so it always reflects only the columns
  // the picker can actually toggle.
  const pickerVisibleColumnIds = React.useMemo(() => {
    const next = new Set(view.table.visibleColumnIds);
    next.delete(ACTIONS_COLUMN_ID);
    return next;
  }, [view.table.visibleColumnIds]);

  const { search, tier } = React.useMemo(
    () =>
      filtersToParams(
        view.filters.type === "structured" ? view.filters.filters : [],
      ),
    [view.filters],
  );

  const orgsQuery = useListOrgs(
    { search, tier },
    view.table.currentPage,
    view.table.pageSize,
  );

  // Tier counts are computed independently of the user's tier filter so the
  // chips always show the Free/Premium breakdown of the *current name
  // search*. Two lightweight `limit: 1` queries piggy-back on the same
  // backend with `total_count` doing the work.
  const freeCountQuery = useListOrgs(
    { search, tier: OrganizationTier.Free },
    0,
    1,
  );
  const premiumCountQuery = useListOrgs(
    { search, tier: OrganizationTier.Premium },
    0,
    1,
  );

  const freeCount = freeCountQuery.totalCount;
  const premiumCount = premiumCountQuery.totalCount;
  const totalCount =
    freeCount !== undefined && premiumCount !== undefined
      ? freeCount + premiumCount
      : undefined;

  let loading: DataTableLoadingState = "idle";
  if (orgsQuery.isLoading) {
    loading = "foreground";
  } else if (orgsQuery.isFetching) {
    loading = "background";
  }

  const rows = orgsQuery.data ?? [];

  return (
    <div className={styles.container}>
      <div className={styles.summary}>
        <SummaryChip label="Total orgs" count={totalCount} variant="total" />
        <SummaryChip label="Free" count={freeCount} />
        <SummaryChip label="Premium" count={premiumCount} variant="premium" />
      </div>

      {orgsQuery.error ? (
        <Alert severity="error" className={styles.errorBanner}>
          {orgsQuery.error.message}
        </Alert>
      ) : null}

      <ResourceView.Root>
        <ResourceView.Actions>
          <ResourceView.Filters
            filterDefinitions={FILTER_DEFINITIONS}
            onFilterSelected={view.commit}
          />
          <ColumnVisibility
            options={COLUMN_PICKER_OPTIONS}
            visibleColumnIds={pickerVisibleColumnIds}
            setColumnVisibility={view.table.setColumnVisibility}
          />
        </ResourceView.Actions>
        <ResourceView.ActiveFilters
          filters={view.filters}
          filterDefinitions={FILTER_DEFINITIONS}
          onRemove={view.removeFilter}
          onClear={view.clearFilters}
          onMatchModeChange={view.setMatchMode}
          onFilterSelected={view.commit}
        />
        <DataTable<OrgRecord>
          table={view.table}
          hasNextPage={orgsQuery.hasNextPage}
          loading={loading}
          columns={columns}
          data={rows}
          noRowsContent="No orgs match the current filters"
          totalRowCount={orgsQuery.totalCount}
        />
      </ResourceView.Root>
    </div>
  );
}
